import { Flex, MenuItem, Text, Tooltip } from '@electricjs/arc';
import { LockStatus } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import LockDeviceModal from '@/components/People/EmployeeProfile/LockDeviceModal';
import UnlockDeviceModal from '@/components/People/EmployeeProfile/UnlockDeviceModal';
import { DeviceAsset } from '@/types/assets';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import LockDeviceWithBitlockerModal from '@/components/People/EmployeeProfile/LockDeviceWithBitlockerModal';
import { WINDOWS } from '@/components/People/EmployeeProfile/helpers/DevicePropertyHelpers';

export enum HigherTierMDMOperationInProgress {
  Wipe = 'wipe',
  Unenroll = 'unenroll',
}

type LockMenuItemProps = {
  employeeName: string;
  deviceAsset: DeviceAsset;
  encryptionEnabled: boolean;
  isProdSupport: boolean;
  lockHasErrored?: boolean;
  lockActionPending?: boolean;
  higherTierMDMOperationInProgress?: HigherTierMDMOperationInProgress;
};

const LockDeviceMenuItem = ({
  employeeName,
  deviceAsset,
  encryptionEnabled,
  isProdSupport,
  lockHasErrored,
  lockActionPending,
  higherTierMDMOperationInProgress: higherTierOperationInProgress,
}: LockMenuItemProps) => {
  const { t } = useTranslation('assets');
  const { openModal, closeModal } = useGlobalUI();
  const organizationId = useGetOrganizationId();

  const rawLockStatus = deviceAsset.device.lockStatus;
  // When lock_status is stuck at REQUESTED after a timeout/error (backend
  // doesn't always reset the column), treat it as Unlocked so the user can retry.
  const lockStatus =
    lockHasErrored && rawLockStatus === LockStatus.Requested
      ? LockStatus.Unlocked
      : rawLockStatus;

  const handleLockDevice = () => {
    // If device is Windows and Bitlocker Encryption is enabled, we need to show the Bitlocker Lock modal
    if (deviceAsset.device.osType === WINDOWS && encryptionEnabled) {
      openModal(
        <LockDeviceWithBitlockerModal
          organizationId={organizationId}
          deviceAsset={deviceAsset}
          employeeName={employeeName}
          onClose={closeModal}
        />
      );
    } else {
      openModal(
        <LockDeviceModal
          organizationId={organizationId}
          deviceAsset={deviceAsset}
          employeeName={employeeName}
          onClose={closeModal}
        />
      );
    }
  };

  const handleUnlockDevice = () => {
    openModal(
      <UnlockDeviceModal
        deviceAsset={deviceAsset}
        employeeName={employeeName}
        onClose={closeModal}
      />
    );
  };

  if (higherTierOperationInProgress) {
    return (
      <Tooltip
        maxWidth="30rem"
        placement="left"
        content={t('assetDetailsButtonMenu.operationInProgress', {
          operation: higherTierOperationInProgress,
        })}>
        <Flex width="100%">
          <MenuItem id="lock-device-menu-item" disabled>
            <Text>{t('assetDetailsButtonMenu.lockDevice')}</Text>
          </MenuItem>
        </Flex>
      </Tooltip>
    );
  }

  const productSupportTooltip = t(
    'assetDetailsButtonMenu.productSupportDisabled'
  );

  // If the lock has been requested then there's nothing to do and should always show the tooltip and have the menu item disabled.
  if (lockStatus === LockStatus.Requested) {
    return (
      <Tooltip
        maxWidth="30rem"
        content={t('assetDetailsButtonMenu.lockScheduled')}
        placement="left">
        <Flex width="100%">
          <MenuItem id="lock-device-menu-item" disabled>
            <Text>{t('assetDetailsButtonMenu.lockPending')}</Text>
          </MenuItem>
        </Flex>
      </Tooltip>
    );
  }

  if (lockStatus === LockStatus.Locked) {
    return (
      <Tooltip
        maxWidth="30rem"
        content={productSupportTooltip}
        placement="left"
        disabled={!isProdSupport}>
        <Flex maxWidth="30rem">
          <MenuItem
            id="unlock-device-menu-item"
            onClick={handleUnlockDevice}
            disabled={isProdSupport}>
            <Text>{t('assetDetailsButtonMenu.unlockDevice')}</Text>
          </MenuItem>
        </Flex>
      </Tooltip>
    );
  }

  if (lockStatus === LockStatus.Unlocked) {
    return (
      <Tooltip
        maxWidth="30rem"
        content={
          lockActionPending
            ? t('assetDetailsButtonMenu.checkingMdmSupport')
            : productSupportTooltip
        }
        placement="left"
        disabled={!isProdSupport && !lockActionPending}>
        <Flex width="100%">
          <MenuItem
            id="lock-device-menu-item"
            onClick={handleLockDevice}
            disabled={isProdSupport || lockActionPending}>
            <Text>{t('assetDetailsButtonMenu.lockDevice')}</Text>
          </MenuItem>
        </Flex>
      </Tooltip>
    );
  }

  return null;
};

export default LockDeviceMenuItem;
